import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import type Store from '@ember-data/store';
import type CompanyModel from 'tio-common/models/company';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import type {
  TasProgramInstance,
  AggregatedProgramInstance,
  OpenSearchResponse,
  ProgramsSearchResult,
} from 'tio-common/models/search';
import { hash, array } from '@ember/helper';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { camelToSnakeCase } from 'tio-common/utils/string-case-conversion';
import { or } from 'tio-ui/utilities';
import {
  getModelTypeFromDataset,
  getDefaultSortFields,
  getEssentialFields,
  getCompanyFilterField,
  AVAILABLE_DATASETS,
} from '../../utils/dataset-utils';
import { RELATIVE_DATE_PRESETS } from 'tio-common/components/forms/relative-date-range-picker';
import type { ApplicationFilterSignature, PaymentFilterSignature } from './types.gts';
import type { ChartDataPoint } from 'tio-common/types/chart';
import type { RelativeDatePreset } from 'tio-common/components/forms/relative-date-range-picker';
import {
  employeeCustomFieldValue,
  employeeCustomLeafFallbackKey,
} from '../../utils/employee-custom-field-resolver';
import { tasCustomFieldValue } from '../../utils/tas-custom-field-resolver';

type SearchAdapter = {
  search: (query: Record<string, unknown>) => Promise<unknown>;
};

interface ProgramInstanceAggregationResult {
  programInstanceId: string;
  employeeId: string;
  employeeName?: string;
  totalApplications: number;
  applicationStateCounts: Record<string, number>;
  sampleDocument?: unknown;
}

interface TopHitsAggregationResult {
  hits?: {
    hits?: Array<{ _source?: TasProgramInstance }>;
  };
}

interface FilterWithTopHitAggregationResult {
  docCount?: number;
  firstHit?: TopHitsAggregationResult;
}

interface ChartData {
  field: string;
  dataPoints: ChartDataPoint[];
  total: number;
}

const DEFAULT_DATASET = 'tas-program-instances';
const VALID_REPORT_DATASET_KEYS = new Set(AVAILABLE_DATASETS.map((dataset) => dataset.key));

const EMPLOYEE_EF_CORE_FIELDS = new Set([
  'employee_ef_fields.employee_id_keyword',
  'employee_ef_fields.employee_payroll_id_keyword',
  'employee_ef_fields.employee_first_name_keyword_analyzable',
  'employee_ef_fields.employee_last_name_keyword_analyzable',
  'employee_ef_fields.employee_title_keyword',
  'employee_ef_fields.employee_email_keyword',
  'employee_ef_fields.employee_person_id_keyword',
  'employee_ef_fields.employee_name_keyword',
  'employee_ef_fields.employee_termination_verified_boolean',
  'employee_ef_fields.employee_fields',
  'employee_ef_fields.employee_custom_fields',
]);

const AGGREGATE_TOP_HIT_SORT = [
  { tas_program_instance_created_at_date: { order: 'desc' as const } },
  { tas_application_created_at_date: { order: 'desc' as const } },
];

export interface ProgramsDataProviderSignature {
  Args: ApplicationFilterSignature &
    PaymentFilterSignature & {
      company?: CompanyModel;
      limit: number;
      page: number;

      // Dataset selection (for multi-dataset support in analytics)
      selectedDataset?: string; // e.g., 'tas-program-instances', 'people', 'pslf-forms'

      // Program filters
      programTemplateId?: string;
      programCreatedAtStart?: string;
      programCreatedAtEnd?: string;
      excludeNonVoucherPrograms?: boolean;
      courseCashProgramsOnly?: boolean;

      // Course filters
      includeCourses?: boolean;
      coursesBeginDate?: string;
      coursesEndDate?: string;

      // Approver filters
      approverId?: string;

      // Employee filters
      employeeId?: string;
      terminatedEmployees?: boolean;
      excludeTermVerified?: boolean;

      // Invoice filters
      includeInvoiceDate?: boolean;

      // Column selection
      selectedColumns?: string[];

      // Sort field (camelCase with :asc/:desc suffix, e.g. 'companyNameKeyword:asc')
      sortField?: string;

      // Generic filters for non-TAS datasets (field key → value, with __gte/__lte for ranges)
      genericFilters?: Record<string, unknown>;

      // Whether the caller has finished hydrating field metadata.
      // When false, search is deferred to avoid startup request churn.
      isReady?: boolean;

      // Program template metadata supplied by parent (avoids duplicate query)
      programTemplates?: TasProgramTemplateModel[];
      isLoadingProgramTemplates?: boolean;
      selectedProgramTemplate?: TasProgramTemplateModel;

      // Callback for updating query parameters
      updateQueryParameters: (params: Record<string, unknown>) => void;
    };
  Blocks: {
    default: [
      {
        // Data and loading states
        data: ProgramsSearchResult;
        isLoading: boolean;
        isDataLoaded: boolean;
        error?: Error;

        // Program templates for filters
        programTemplates: TasProgramTemplateModel[];
        isLoadingProgramTemplates: boolean;
        selectedProgramTemplate?: TasProgramTemplateModel;

        // Actions
        downloadCsv: () => Promise<unknown>;
        refresh: () => void;
        updateQueryParameters: (params: Record<string, unknown>) => void;

        // Chart-specific functionality
        buildChartData: (field: string) => ChartData | null;
        getAvailableChartFields: () => Array<{ key: string; label: string }>;

        // Utility getters
        totalRecords: number;
        executionTime: number;
      },
    ];
  };
}

export default class ProgramsDataProviderComponent extends Component<ProgramsDataProviderSignature> {
  @service declare store: Store;

  private lastSearchRequestKey?: string;
  private lastSearchRequestPromise?: Promise<
    OpenSearchResponse<TasProgramInstance | Record<string, unknown>>
  >;

  private isTasCustomFlattenedColumn(columnKey: string): boolean {
    return (
      columnKey.startsWith('tasProgramInstanceCustomFieldsFlattened.') ||
      columnKey.startsWith('tasApplicantCustomFieldsFlattened.') ||
      columnKey.startsWith('tasApplicationCustomFieldsFlattened.') ||
      columnKey.startsWith('tasCourseCustomFieldsFlattened.') ||
      columnKey.startsWith('tasLimitCustomFieldsFlattened.')
    );
  }

  private aggregateFallbackCustomColumns(selectedColumns: string[] = []): string[] {
    return [
      ...new Set(selectedColumns.filter((column) => this.isTasCustomFlattenedColumn(column))),
    ];
  }

  private aggregateFallbackAggregationName(index: number): string {
    return `firstNonEmptyCustomField${index}`;
  }

  private resolveModelType(dataset: string): string {
    if (dataset === 'tas-program-instances' && this.args.showIndividualApplications) {
      return 'TasApplication';
    }

    return getModelTypeFromDataset(dataset);
  }

  private getNestedValue(record: Record<string, unknown>, path: string): unknown {
    return path
      .split('.')
      .filter(Boolean)
      .reduce<unknown>((acc, segment) => {
        if (!acc || typeof acc !== 'object') {
          return undefined;
        }

        return (acc as Record<string, unknown>)[segment];
      }, record);
  }

  private setNestedValue(record: Record<string, unknown>, path: string, value: unknown): void {
    const segments = path.split('.').filter(Boolean);
    if (segments.length === 0) {
      return;
    }

    let current: Record<string, unknown> = record;
    segments.slice(0, -1).forEach((segment) => {
      const existing = current[segment];
      if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
        current[segment] = {};
      }

      current = current[segment] as Record<string, unknown>;
    });

    const leaf = segments.at(-1);
    if (!leaf) {
      return;
    }

    current[leaf] = value;
  }

  private hasMeaningfulAggregateValue(value: unknown): boolean {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  private mergeAggregateFallbackField(
    target: Record<string, unknown>,
    sourceDocument: TasProgramInstance,
    columnKey: string
  ): void {
    const existingValue = this.resolveAggregateColumnValue(target, columnKey);
    if (this.hasMeaningfulAggregateValue(existingValue)) {
      return;
    }

    const sourceValue = this.resolveAggregateColumnValue(
      sourceDocument as Record<string, unknown>,
      columnKey
    );
    if (!this.hasMeaningfulAggregateValue(sourceValue)) {
      return;
    }

    this.setNestedValue(target, columnKey, sourceValue);
  }

  private resolveAggregateColumnValue(record: Record<string, unknown>, columnKey: string): unknown {
    const directValue = this.getNestedValue(record, columnKey);
    if (this.hasMeaningfulAggregateValue(directValue)) {
      return directValue;
    }

    if (
      columnKey.startsWith('employeeEfFields.') &&
      !columnKey.startsWith('employeeEfFields.employeeCustomFields.')
    ) {
      const leaf = columnKey.split('.').filter(Boolean).at(-1);
      if (leaf) {
        const employeeEf =
          (record.employeeEfFields as Record<string, unknown> | undefined) ||
          (record['employee_ef_fields'] as Record<string, unknown> | undefined);

        const directEfValue = employeeCustomFieldValue(employeeEf, leaf);
        if (this.hasMeaningfulAggregateValue(directEfValue)) {
          return directEfValue;
        }

        const customFields =
          (employeeEf?.employeeCustomFields as Record<string, unknown> | undefined) ||
          (employeeEf?.employee_custom_fields as Record<string, unknown> | undefined);
        const nestedEfValue = employeeCustomFieldValue(customFields, leaf);
        if (this.hasMeaningfulAggregateValue(nestedEfValue)) {
          return nestedEfValue;
        }
      }
    }

    const tasValue = tasCustomFieldValue(record, columnKey);
    if (this.hasMeaningfulAggregateValue(tasValue)) {
      return tasValue;
    }

    return directValue;
  }

  private extractTopHitSource(
    aggregation?: TopHitsAggregationResult
  ): TasProgramInstance | undefined {
    const source = aggregation?.hits?.hits?.[0]?._source;
    return source ? this.normalizeTasCustomFlattenedContainers(source) : undefined;
  }

  private normalizeLookupKey(key: string): string {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private canonicalTasCustomLeaf(containerKey: string, rawLeaf: string): string | undefined {
    const normalizedLeaf = this.normalizeLookupKey(rawLeaf);

    if (
      containerKey === 'tasProgramInstanceCustomFieldsFlattened' ||
      containerKey === 'tasApplicantCustomFieldsFlattened' ||
      containerKey === 'tasLimitCustomFieldsFlattened'
    ) {
      const syntheticMatch = normalizedLeaf.match(
        /customfieldtas(?:programinstance|applicant|limit)(\d+)$/
      );
      if (syntheticMatch?.[1]) {
        return `customFieldTasProgramInstance_${syntheticMatch[1]}`;
      }

      const match = normalizedLeaf.match(
        /tas(?:programinstance|applicant|limit)customfield(?:tas(?:programinstance|applicant|limit))?(\d+)$/
      );
      if (match?.[1]) {
        return `customFieldTasProgramInstance_${match[1]}`;
      }
    }

    if (containerKey === 'tasApplicationCustomFieldsFlattened') {
      const templateMatch = normalizedLeaf.match(/customfieldtasprogramtemplate(\d+)$/);
      if (templateMatch?.[1]) {
        return `customFieldTasApplication_${templateMatch[1]}`;
      }

      const syntheticMatch = normalizedLeaf.match(/customfieldtasapplication(\d+)$/);
      if (syntheticMatch?.[1]) {
        return `customFieldTasApplication_${syntheticMatch[1]}`;
      }

      const match = normalizedLeaf.match(/tasapplicationcustomfield(?:tasapplication)?(\d+)$/);
      if (match?.[1]) {
        return `customFieldTasApplication_${match[1]}`;
      }
    }

    if (containerKey === 'tasCourseCustomFieldsFlattened') {
      const templateMatch = normalizedLeaf.match(/customfieldtasprogramtemplate(\d+)$/);
      if (templateMatch?.[1]) {
        return `customFieldTasCourse_${templateMatch[1]}`;
      }

      const syntheticMatch = normalizedLeaf.match(/customfieldtascourse(\d+)$/);
      if (syntheticMatch?.[1]) {
        return `customFieldTasCourse_${syntheticMatch[1]}`;
      }

      const match = normalizedLeaf.match(/tascoursecustomfield(?:tascourse)?(\d+)$/);
      if (match?.[1]) {
        return `customFieldTasCourse_${match[1]}`;
      }
    }

    return undefined;
  }

  private normalizeTasCustomFlattenedContainers(sampleDoc: TasProgramInstance): TasProgramInstance {
    const output = { ...(sampleDoc as Record<string, unknown>) };
    const containerAliases: Record<string, string[]> = {
      tasProgramInstanceCustomFieldsFlattened: ['tasApplicantCustomFieldsFlattened'],
      tasApplicantCustomFieldsFlattened: ['tasProgramInstanceCustomFieldsFlattened'],
      tasApplicationCustomFieldsFlattened: [],
      tasCourseCustomFieldsFlattened: [],
      tasLimitCustomFieldsFlattened: ['tasProgramInstanceCustomFieldsFlattened'],
    };
    const containerVariants = (key: string): string[] => {
      const snake = key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
      const camel = key.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase());
      return Array.from(new Set([key, snake, camel]));
    };
    const containers = [
      'tasProgramInstanceCustomFieldsFlattened',
      'tasApplicantCustomFieldsFlattened',
      'tasApplicationCustomFieldsFlattened',
      'tasCourseCustomFieldsFlattened',
      'tasLimitCustomFieldsFlattened',
    ] as const;

    containers.forEach((containerKey) => {
      const aliasKeys = containerAliases[containerKey] || [];
      const candidateKeys = [
        ...containerVariants(containerKey),
        ...aliasKeys.flatMap((alias) => containerVariants(alias)),
      ];
      const containerValue = candidateKeys
        .map((key) => output[key])
        .find((value) => value && typeof value === 'object' && !Array.isArray(value));
      if (!containerValue || typeof containerValue !== 'object' || Array.isArray(containerValue)) {
        return;
      }

      const container = containerValue as Record<string, unknown>;
      const normalizedContainer: Record<string, unknown> = { ...container };

      Object.entries(container).forEach(([candidateKey, value]) => {
        const leaf = candidateKey.split('.').filter(Boolean).at(-1) || candidateKey;
        const canonicalLeaf = this.canonicalTasCustomLeaf(containerKey, leaf);
        if (canonicalLeaf && normalizedContainer[canonicalLeaf] === undefined) {
          normalizedContainer[canonicalLeaf] = value;
        }
      });

      output[containerKey] = normalizedContainer;
    });

    if (
      !output.tasCourseCustomFieldsFlattened ||
      typeof output.tasCourseCustomFieldsFlattened !== 'object' ||
      Array.isArray(output.tasCourseCustomFieldsFlattened)
    ) {
      const courses =
        (output.tasApplicationCoursesFields as Array<Record<string, unknown>> | undefined) ||
        (output.tas_application_courses_fields as Array<Record<string, unknown>> | undefined) ||
        [];
      const firstWithCustom = courses.find((course) => {
        const courseFields = course?.tasCourseFields as Record<string, unknown> | undefined;
        return (
          courseFields?.tasCourseCustomFieldsFlattened &&
          typeof courseFields.tasCourseCustomFieldsFlattened === 'object' &&
          !Array.isArray(courseFields.tasCourseCustomFieldsFlattened)
        );
      });

      if (firstWithCustom) {
        const courseFields = firstWithCustom.tasCourseFields as Record<string, unknown>;
        // Use the first course custom fields as a display fallback for application rows.
        output.tasCourseCustomFieldsFlattened = courseFields.tasCourseCustomFieldsFlattened;
      }
    }

    return output as TasProgramInstance;
  }

  private get selectedDatasetKey(): string {
    const requestedDataset = this.args.selectedDataset?.trim();
    if (!requestedDataset) {
      return DEFAULT_DATASET;
    }

    return VALID_REPORT_DATASET_KEYS.has(requestedDataset) ? requestedDataset : DEFAULT_DATASET;
  }

  get companyId() {
    return this.args.company?.id;
  }

  // Transform program-instance aggregation logic from OpenSearchResponseBuilder
  private transformProgramInstanceAggregation(
    responseBuilder: OpenSearchResponseBuilder<TasProgramInstance>
  ): ProgramInstanceAggregationResult[] {
    const employees = responseBuilder.getTermsAggregation('employees');
    const fallbackCustomColumns = this.aggregateFallbackCustomColumns(
      this.args.selectedColumns || []
    );

    return employees.map((employeeBucket) => {
      // Get application state counts - API returns camelized field names
      const applicationStates = employeeBucket.aggregations?.applicationStates as
        | { buckets?: Array<Record<string, unknown>> }
        | undefined;
      const stateCounts: Record<string, number> = {};

      if (applicationStates?.buckets) {
        applicationStates.buckets.forEach((stateBucket: Record<string, unknown>) => {
          stateCounts[String(stateBucket.key)] = stateBucket.docCount as number;
        });
      }

      // Get sample document - API returns camelized field names in _source
      let sampleDoc = this.extractTopHitSource(
        employeeBucket.aggregations?.sampleDocument as TopHitsAggregationResult | undefined
      );

      if (fallbackCustomColumns.length > 0) {
        const mergedSampleDoc = {
          ...((sampleDoc as Record<string, unknown> | undefined) || {}),
        } as Record<string, unknown>;

        fallbackCustomColumns.forEach((columnKey, index) => {
          const fallbackAggregation = employeeBucket.aggregations?.[
            this.aggregateFallbackAggregationName(index)
          ] as FilterWithTopHitAggregationResult | undefined;
          const fallbackDocument = this.extractTopHitSource(fallbackAggregation?.firstHit);

          if (fallbackDocument) {
            this.mergeAggregateFallbackField(mergedSampleDoc, fallbackDocument, columnKey);
          }
        });

        if (Object.keys(mergedSampleDoc).length > 0) {
          sampleDoc = mergedSampleDoc as TasProgramInstance;
        }
      }

      return {
        programInstanceId: sampleDoc?.tasProgramInstanceIdKeyword || employeeBucket.key,
        employeeId: sampleDoc?.employeeIdKeyword || '',
        employeeName: sampleDoc?.employeeNameKeyword,
        totalApplications: employeeBucket.count,
        applicationStateCounts: stateCounts,
        sampleDocument: sampleDoc,
      };
    });
  }

  get results(): ProgramsSearchResult {
    const response = this.searchData.value;
    const isLoading = this.searchData.isLoading;
    if (!response) {
      return { items: [], total: 0, took: 0, isLoading };
    }

    const responseBuilder = OpenSearchResponseBuilder.from(response);

    const dataset = this.selectedDatasetKey;

    if (this.args.showIndividualApplications || dataset !== 'tas-program-instances') {
      // Individual/document mode - extract documents from hits and return SearchModelWith format
      const rawItems = responseBuilder.getDocuments() as Array<Record<string, unknown>>;
      const items =
        dataset === 'tas-program-instances'
          ? rawItems.map((item) =>
              this.normalizeTasCustomFlattenedContainers(item as TasProgramInstance)
            )
          : rawItems;
      return {
        items,
        total: responseBuilder.getTotalCount(),
        took: responseBuilder.getExecutionTime(),
        isLoading,
      };
    } else {
      // Aggregated mode - use our local transformer for employee aggregations
      // Type assertion is safe: aggregated mode is only used for TAS datasets
      const employeeResults = this.transformProgramInstanceAggregation(
        responseBuilder as OpenSearchResponseBuilder<TasProgramInstance>
      );

      // Map to the AggregatedProgramInstance format expected by the component
      const allItems: AggregatedProgramInstance[] = employeeResults.map((result) => {
        const sampleDoc = ((result.sampleDocument as TasProgramInstance | undefined) ??
          {}) as Record<string, unknown>;
        const programInstanceId = result.programInstanceId || `unknown-${result.employeeId}`;
        return {
          // NOTE: sampleDoc spread may overwrite explicitly-set keys below if they
          // exist in the OpenSearch _source. Consider spreading it last or picking
          // only the keys that are actually needed.
          ...sampleDoc,
          employeeId: result.employeeId,
          // API returns camelCase field names in response
          employeeNameKeyword:
            (result.sampleDocument as TasProgramInstance)?.employeeNameKeyword ||
            `Employee ${result.employeeId || 'unknown'}`,
          employeePayrollIdKeyword:
            (result.sampleDocument as TasProgramInstance)?.employeePayrollIdKeyword || '',
          programInstanceId,
          tasProgramInstanceIdKeyword: programInstanceId, // Canonical field name for column mapping
          tasProgramInstanceCreatedAtDate:
            (result.sampleDocument as TasProgramInstance)?.tasProgramInstanceCreatedAtDate || '',
          // Program name from template (correct field)
          tasProgramTemplateFieldProgramNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program',
          // DEPRECATED: Alias for backward compatibility with old column selections
          // Users may have selected this field, but the correct field is tasProgramTemplateFieldProgramNameKeyword
          tasProgramInstanceFieldNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramTemplateFieldProgramNameKeyword ||
            (result.sampleDocument as TasProgramInstance)?.tasProgramInstanceFieldNameKeyword ||
            'Unknown Program',
          tasProgramInstanceFieldSchoolInstitutionNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramInstanceFieldSchoolInstitutionNameKeyword || '',
          applicationStateCounts: result.applicationStateCounts,
          totalApplications: result.totalApplications,
        };
      });

      // Apply client-side pagination to the aggregated results
      const { page = 1, limit = 25 } = this.args;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedItems = allItems.slice(startIndex, endIndex);

      return {
        items: paginatedItems,
        total: allItems.length, // Total number of unique employees (aggregation buckets)
        took: responseBuilder.getExecutionTime(),
        isLoading,
      };
    }
  }

  /**
   * Calculate actual date range based on relative preset
   * Returns the dates to use for the query (recalculated if preset is active)
   */
  getApplicationDateRange(): { start?: string; end?: string } {
    // If a relative preset is selected (not 'custom'), recalculate the dates
    const presetId = this.args.applicationDateRangePreset;

    if (presetId && presetId !== 'custom') {
      const preset = RELATIVE_DATE_PRESETS.find((p: RelativeDatePreset) => p.id === presetId);
      if (preset) {
        const { start, end } = preset.getDates();
        return { start, end };
      }
    }

    // Otherwise, use the stored dates as-is (custom mode or no preset)
    return {
      start: this.args.applicationCreatedAtStart,
      end: this.args.applicationCreatedAtEnd,
    };
  }

  buildSearchQuery() {
    const dataset = this.selectedDatasetKey;

    if (dataset !== 'tas-program-instances') {
      const companyFilterField = getCompanyFilterField(dataset);
      if (!companyFilterField) {
        throw new Error(`Missing company filter field for dataset: ${dataset}`);
      }

      const builder = new OpenSearchQueryBuilder();
      builder.mustTerm(companyFilterField, this.companyId);

      // Apply generic filters from the DatasetFilters component
      const filters = this.args.genericFilters;
      if (filters) {
        // Collect range filter fields (keys ending in __gte or __lte)
        const rangeFields = new Map<string, { gte?: string; lte?: string }>();

        for (const [key, value] of Object.entries(filters)) {
          if (value === undefined || value === null || value === '') continue;

          if (key.endsWith('__gte') || key.endsWith('__lte')) {
            // Range filter — extract field name and bound
            const fieldKey = key.replace(/__(?:gte|lte)$/, '');
            // Convert camelCase field key to snake_case for OpenSearch
            const snakeField = fieldKey.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
            if (!rangeFields.has(snakeField)) {
              rangeFields.set(snakeField, {});
            }
            const range = rangeFields.get(snakeField)!;
            if (key.endsWith('__gte')) {
              range.gte = String(value);
            } else {
              range.lte = String(value);
            }
          } else if (typeof value === 'boolean') {
            builder.mustTerm(
              key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`),
              value
            );
          } else if (Array.isArray(value) && value.length > 0) {
            builder.mustTerms(
              key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`),
              value
            );
          } else if (typeof value === 'string') {
            builder.mustTerm(
              key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`),
              value
            );
          }
        }

        // Apply collected range filters
        for (const [field, bounds] of rangeFields) {
          builder.range(field, bounds.gte, bounds.lte);
        }
      }

      return builder;
    }

    // Get the actual date range (recalculated if using a relative preset)
    const applicationDateRange = this.getApplicationDateRange();

    return new OpenSearchQueryBuilder()
      .mustTerm('company_id', this.companyId)
      .mustTerm('tas_program_template_id_keyword', this.args.programTemplateId)
      .when(this.args.excludeNonVoucherPrograms, (b) => {
        b.mustTerm('tas_program_template_field_voucher_program_boolean', true);
      })
      .when(this.args.courseCashProgramsOnly, (b) => {
        b.addFilter({
          range: {
            tas_application_course_cash_reserved_amount_integer: { gt: 0 },
          },
        });
      })
      .range(
        'tas_program_instance_created_at_date',
        this.args.programCreatedAtStart,
        this.args.programCreatedAtEnd
      )
      .range(
        'tas_application_created_at_date',
        applicationDateRange.start,
        applicationDateRange.end
      )
      .when(this.args.coursesBeginDate || this.args.coursesEndDate, (b) => {
        // coursesBeginDate and coursesEndDate define a date range for filtering courses
        // that both START and END within the specified date range
        // e.g., "Show courses where begin date >= Oct 13 AND begin date <= Oct 14 AND end date <= Oct 14"
        b.exists('tas_application_field_courses_begin_date_date')
          .range(
            'tas_application_field_courses_begin_date_date',
            this.args.coursesBeginDate,
            this.args.coursesEndDate
          )
          .exists('tas_application_field_courses_end_date_date')
          .range(
            'tas_application_field_courses_end_date_date',
            undefined,
            this.args.coursesEndDate
          );
      })
      .mustTerm('employee_id', this.args.employeeId)
      .mustTerm('employment_termination_verified', this.args.excludeTermVerified)
      .mustTerm('tas_application_possible_reviewers', this.args.approverId)
      .when(this.args.terminatedEmployees, (b) => {
        b.range('employment_end_date', undefined, new Date().toISOString().split('T')[0]);
      })
      .mustTermFlexible(
        'tas_application_state_keyword',
        this.args.applicationStates?.split(',').filter(Boolean)
      )
      .when(this.args.paymentStates, (b) => {
        const paymentStateGroups = this.args.paymentStates?.split(',').map((state) => {
          const field = state.replace(/-/g, '_');
          return [
            { exists: { field } },
            {
              range: {
                [field]: {
                  [this.args.excludeNonVoucherPrograms ? 'gte' : 'gt']: 0,
                },
              },
            },
          ];
        });
        if (!paymentStateGroups || !paymentStateGroups.length) {
          return;
        }
        b.shouldEach(paymentStateGroups).setMinimumShouldMatch(1);
      })
      .when(this.args.paymentYear, (b) => {
        const year = Number(this.args.paymentYear);
        b.range('tas_application_payment_last_date_date', `${year}-01-01`, `${year + 1}-01-01`);
      });
  }

  private selectedSourceColumns(selectedColumns: string[]): string[] {
    const snakeCaseColumns = selectedColumns.map((column) => camelToSnakeCase(column));

    const selectedEmployeeEfLeaves = snakeCaseColumns
      .filter((column) => /^employee_ef_fields\.[^.]+$/.test(column))
      .filter((column) => !EMPLOYEE_EF_CORE_FIELDS.has(column))
      .map((column) => column.replace('employee_ef_fields.', ''));

    const employeeEfCustomNestedFields = selectedEmployeeEfLeaves.flatMap((leaf) => {
      const strippedLeaf = employeeCustomLeafFallbackKey(leaf);

      return Array.from(
        new Set([
          `employee_ef_fields.employee_custom_fields.${leaf}`,
          `employee_ef_fields.employee_custom_fields.${strippedLeaf}`,
        ])
      );
    });

    const includeEmployeeCustomFieldsContainer =
      selectedEmployeeEfLeaves.length > 0 ? ['employee_ef_fields.employee_custom_fields.*'] : [];

    const tasCustomFlattenedContainers = snakeCaseColumns
      .filter(
        (column) =>
          column.startsWith('tas_program_instance_custom_fields_flattened.') ||
          column.startsWith('tas_applicant_custom_fields_flattened.') ||
          column.startsWith('tas_application_custom_fields_flattened.') ||
          column.startsWith('tas_course_custom_fields_flattened.') ||
          column.startsWith('tas_limit_custom_fields_flattened.')
      )
      .map((column) => {
        const dotIndex = column.indexOf('.');
        const container = dotIndex >= 0 ? column.slice(0, dotIndex) : column;
        return `${container}.*`;
      });

    // Course object fields live in a nested array — request the whole container
    const courseObjectContainer = snakeCaseColumns.some((col) =>
      col.startsWith('tas_application_courses_object.')
    )
      ? ['tas_application_courses_object.*']
      : [];

    return [
      ...new Set([
        ...snakeCaseColumns,
        ...employeeEfCustomNestedFields,
        ...includeEmployeeCustomFieldsContainer,
        ...tasCustomFlattenedContainers,
        ...courseObjectContainer,
      ]),
    ];
  }

  private buildRawOpenSearchQuery() {
    const baseQuery = this.buildSearchQuery();
    const dataset = this.selectedDatasetKey;

    if (this.args.showIndividualApplications || dataset !== 'tas-program-instances') {
      // Individual/document mode - apply pagination to the OpenSearch query
      const { page = 1, limit = 25 } = this.args;

      // Use user-selected sort field if provided, otherwise dataset defaults.
      // Sort field arrives as camelCase (FE convention), convert to snake_case for OpenSearch.
      let sortFields: string[];
      if (this.args.sortField) {
        const snakeSort = this.args.sortField.replace(/([A-Z])/g, (m) => `_${m.toLowerCase()}`);
        sortFields = [snakeSort];
      } else {
        sortFields = getDefaultSortFields(dataset);
      }
      baseQuery.setPagination(page, limit).setSort(sortFields);

      // Use builder's selectColumns for generic case conversion
      if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
        const essentialFields = getEssentialFields(dataset);
        baseQuery.selectColumns(
          this.selectedSourceColumns(this.args.selectedColumns),
          essentialFields
        );
      }

      const query = baseQuery.build();

      return {
        query,
        aggs: {},
      };
    } else {
      // Aggregated mode - use the query builder's aggregation methods
      // Note: In aggregation mode, we need to fetch ALL buckets to get accurate total count
      // We'll handle client-side pagination by slicing the results array
      const aggregatedQuery = baseQuery
        .setPagination(0, 0) // Set size to 0 for aggregation-only queries (we only want aggregations)
        .addTermsAggregation('employees', 'tas_program_instance_id_keyword', 10000) // Fetch all program-instance buckets
        .addNestedAggregation('employees', 'application_states', {
          terms: {
            field: 'tas_application_state_keyword',
            size: 50,
          },
        });

      // Determine fields for sample document in aggregation
      // Use snake_case for OpenSearch field names in queries
      let sampleDocumentFields = [
        'employee_id_keyword',
        'employee_name_keyword',
        'employee_payroll_id_keyword',
        'tas_program_instance_id_keyword',
        'tas_program_instance_created_at_date',
        'tas_program_template_field_program_name_keyword',
        'tas_program_instance_field_school_institution_name_keyword',
      ];

      // If specific columns are selected, use those plus essential aggregation fields
      if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
        const snakeCaseColumns = this.selectedSourceColumns(this.args.selectedColumns);
        const fallbackCustomColumns = this.aggregateFallbackCustomColumns(
          this.args.selectedColumns
        );

        const essentialAggregationFields = [
          // Core fields needed for aggregation display
          'employee_id_keyword',
          'employee_name_keyword',
          'employee_payroll_id_keyword',
          'tas_program_instance_id_keyword',
          'tas_program_template_field_program_name_keyword',
          'tas_program_instance_field_school_institution_name_keyword',
          // State and date fields for fallback logic
          'tas_application_state_keyword',
          'tas_program_instance_state_keyword',
          'tas_application_created_at_date',
          'tas_program_instance_created_at_date',
        ];

        sampleDocumentFields = [...new Set([...snakeCaseColumns, ...essentialAggregationFields])];

        fallbackCustomColumns.forEach((columnKey, index) => {
          // For flattened-container column keys (container.leaf), only snake-case
          // the container portion.  The leaf key is stored verbatim (camelCase)
          // inside the flat_object, so converting it would produce a path that
          // does not exist in OpenSearch and the exists filter would return zero hits.
          const firstDotIndex = columnKey.indexOf('.');
          const existsField =
            firstDotIndex >= 0
              ? `${camelToSnakeCase(columnKey.slice(0, firstDotIndex))}.${columnKey.slice(firstDotIndex + 1)}`
              : camelToSnakeCase(columnKey);
          aggregatedQuery.addNestedAggregation(
            'employees',
            this.aggregateFallbackAggregationName(index),
            {
              filter: {
                exists: {
                  field: existsField,
                },
              },
              aggs: {
                firstHit: {
                  top_hits: {
                    size: 1,
                    _source: {
                      includes: this.selectedSourceColumns([columnKey]),
                    },
                    sort: AGGREGATE_TOP_HIT_SORT,
                  },
                },
              },
            }
          );
        });
      }

      aggregatedQuery.addNestedAggregation('employees', 'sample_document', {
        top_hits: {
          size: 1,
          _source: {
            includes: sampleDocumentFields,
          },
          sort: AGGREGATE_TOP_HIT_SORT,
        },
      });

      const builtQuery = aggregatedQuery.build();

      return {
        query: builtQuery,
        aggs: builtQuery.aggs || {},
      };
    }
  }

  searchData = trackedFunction(this, async () => {
    if (this.args.isReady === false) {
      this.lastSearchRequestKey = undefined;
      this.lastSearchRequestPromise = undefined;
      return undefined as unknown as OpenSearchResponse<
        TasProgramInstance | Record<string, unknown>
      >;
    }

    // Determine model type based on selected dataset
    const dataset = this.selectedDatasetKey;
    const modelType = this.resolveModelType(dataset);

    const rawQuery = this.buildRawOpenSearchQuery();

    const base = {
      'model-type': modelType,
      searchBody: rawQuery.query,
      aggregations: rawQuery.aggs,
    };

    const requestKey = JSON.stringify(base);
    if (this.lastSearchRequestKey === requestKey && this.lastSearchRequestPromise) {
      return await this.lastSearchRequestPromise;
    }

    this.lastSearchRequestKey = requestKey;
    const searchAdapter = this.store.adapterFor('search') as unknown as SearchAdapter;
    const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    this.lastSearchRequestPromise = searchAdapter.search(base).then((content) => {
      const elapsedMs =
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime;
      if (elapsedMs > 2000) {
        console.warn('[TAS Report] Search request slow', {
          elapsedMs: Math.round(elapsedMs),
          dataset,
          modelType,
          selectedColumns: this.args.selectedColumns?.length ?? 0,
        });
      }
      return content as OpenSearchResponse<TasProgramInstance | Record<string, unknown>>;
    });

    try {
      return await this.lastSearchRequestPromise;
    } catch (error) {
      if (this.lastSearchRequestKey === requestKey) {
        this.lastSearchRequestPromise = undefined;
      }
      throw error;
    }
  });

  downloadCsv = async () => {
    // Note: companyId is optional - global analytics (isGlobalContext=true) doesn't require it
    const rawQuery = this.buildRawOpenSearchQuery();
    const dataset = this.selectedDatasetKey;
    const modelType = this.resolveModelType(dataset);

    const base = {
      'model-type': modelType,
      searchBody: rawQuery.query,
      aggregations: rawQuery.aggs,
      csv_export: true,
      aggregated_mode: !this.args.showIndividualApplications,
    };

    return await this.store.adapterFor('search').downloadCsv(base);
  };

  refresh = () => {
    this.searchData.retry();
  };

  updateQueryParameters = (params: Record<string, unknown>) => {
    // Convert null values to null/undefined to ensure query parameters are removed from URL
    const processedParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => {
        // Convert null values to null to remove query parameters
        if (value === null || value === undefined) {
          return [key, null];
        }
        return [key, value];
      })
    );

    this.args.updateQueryParameters(processedParams);
  };

  buildChartData = (field: string): ChartData | null => {
    const response = this.searchData.value;
    if (!response) return null;

    const responseBuilder = OpenSearchResponseBuilder.from(response);

    // For aggregated mode, extract data from the employee aggregations
    if (!this.args.showIndividualApplications) {
      // Type assertion is safe: aggregated mode is only used for TAS datasets
      const employeeResults = this.transformProgramInstanceAggregation(
        responseBuilder as OpenSearchResponseBuilder<TasProgramInstance>
      );

      if (field === 'application_state') {
        const stateAggregate: Record<string, number> = {};
        let total = 0;

        employeeResults.forEach((result) => {
          Object.entries(result.applicationStateCounts).forEach(([state, count]) => {
            stateAggregate[state] = (stateAggregate[state] || 0) + count;
            total += count;
          });
        });

        const dataPoints: ChartDataPoint[] = Object.entries(stateAggregate).map(([key, value]) => ({
          category: key,
          value,
          count: value,
          metadata: {
            label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          },
        }));

        return { field, dataPoints, total };
      }

      if (field === 'employee_name') {
        const dataPoints: ChartDataPoint[] = employeeResults.map((result) => ({
          category: result.employeeId,
          value: result.totalApplications,
          count: result.totalApplications,
          metadata: {
            label: result.employeeName || `Employee ${result.employeeId}`,
          },
        }));

        const total = dataPoints.reduce((sum, point) => sum + point.value, 0);
        return { field, dataPoints, total };
      }
    }

    // For individual mode, build aggregations from the documents
    const items = responseBuilder.getDocuments() as Array<Record<string, unknown>>;

    if (field === 'application_state') {
      const stateAggregate: Record<string, number> = {};

      items.forEach((item) => {
        const state =
          (item as { tasApplicationStateKeyword?: string }).tasApplicationStateKeyword || 'unknown';
        stateAggregate[state] = (stateAggregate[state] || 0) + 1;
      });

      const dataPoints: ChartDataPoint[] = Object.entries(stateAggregate).map(([key, value]) => ({
        category: key,
        value,
        count: value,
        metadata: {
          label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        },
      }));

      return { field, dataPoints, total: items.length };
    }

    return null;
  };

  getAvailableChartFields = () => {
    return [
      { key: 'application_state', label: 'Application State' },
      { key: 'employee_name', label: 'Employee Name' },
      { key: 'employee_payroll_id_keyword', label: 'Payroll ID' },
      { key: 'program_name', label: 'Program Name' },
    ];
  };

  get totalRecords() {
    return this.results.total || 0;
  }

  get executionTime() {
    return this.results.took || 0;
  }

  get programTemplates() {
    return this.args.programTemplates || [];
  }

  get isLoadingProgramTemplates() {
    return this.args.isLoadingProgramTemplates || false;
  }

  get selectedProgramTemplate() {
    return this.args.selectedProgramTemplate;
  }

  get searchError(): Error | undefined {
    const error = this.searchData.error;
    if (!error || (typeof error === 'object' && Object.keys(error).length === 0)) {
      return undefined;
    }
    if (error instanceof Error) {
      return error;
    }
    // Convert generic error object to Error instance
    return new Error(JSON.stringify(error));
  }

  <template>
    {{yield
      (hash
        data=this.results
        isLoading=this.searchData.isLoading
        isDataLoaded=this.searchData.isFinished
        error=this.searchError
        programTemplates=(or this.programTemplates (array))
        isLoadingProgramTemplates=this.isLoadingProgramTemplates
        selectedProgramTemplate=this.selectedProgramTemplate
        downloadCsv=this.downloadCsv
        refresh=this.refresh
        updateQueryParameters=this.updateQueryParameters
        buildChartData=this.buildChartData
        getAvailableChartFields=this.getAvailableChartFields
        totalRecords=this.totalRecords
        executionTime=this.executionTime
      )
    }}
  </template>
}
